import {
  Component, HostListener, Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import {Store} from "@ngrx/store";
import * as fromStore from '../../../../../store';
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {BoosterService} from "../../services/booster.service";
import {TypeUtils} from "../../../../../shared/utils/type-utils";
import {Utils} from "../../../../../shared/utils/utils";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";
import tooltips from "./advanced-settings-tooltips.json";
import {Subject} from "rxjs";
import {
  NotificationDialogComponent
} from "../../../../../shared/components/dialogs/notification-dialog/notification-dialog.component";
import {TextUtils} from "../../../../../shared/utils/text-utils";
import {DialogService} from "../../../../../../ng2-bootstrap-modal/dialog.service";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";
import {DbModalService} from "../../../../../modules/modal/modal";
import {
  OneWeekFreeDemoUpgradeComponent
} from "../../../../../shared/components/dialogs/one-month-free/components/one-week-free-demo-upgrade/one-week-free-demo-upgrade.component";
import {BuilderSectionsInteractionService} from "../../../../../builder-v2/builder-sections-interaction.service";

@Component({
    selector: 'db-booster-advanced-settings',
    templateUrl: './advanced-settings.component.html',
    styleUrls: ['./advanced-settings.component.scss'],
    standalone: false
})
export class BoosterAdvancedSettingsComponent implements OnInit, OnDestroy {

  @Input() websiteId: number;
  @Input() testModeEnabled: boolean;
  @Input() disableOptions: boolean;
  @Input() demoUser: boolean;

  public formGroup: FormGroup;
  public formGroupData: any;
  public formTooltips: any;
  public loading: boolean;
  public lazyLoad: boolean;
  public disableBtn: boolean = true;
  public loadingReset: boolean;
  public dataLoaded: boolean = false;
  public allSettingsData: any;
  public selectedCssOptions: Array<string> = [];
  public selectedJsOptions: Array<string> = [];
  public excludeFiles: Array<string> = [];
  public excludeCss: Array<string> = [];
  public excludeAggregationFiles: Array<string> = [];
  public defaultExcludeFiles: Array<string> = [];
  public defaultExcludeCss: Array<string> = [];
  public defaultExcludeAggregationFiles: Array<string> = [];
  public addedExcludedElementValue: FormControl = new FormControl("", [Validators.pattern('^([/\w .-]*)*/?$')]);
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  public boosterUpdateAvailable: boolean;
  public hasChange: boolean;
  public companyName: string = '10Web';


  constructor(
    private _store: Store<fromStore.DbStoreState>,
    private _boosterService: BoosterService,
    private _dialogService: DialogService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _modalService: DbModalService,
    private _builderSectionsInteractionService: BuilderSectionsInteractionService
  ) {}

  ngOnInit() {
    /*!this.dataLoaded && this._setFormGroupData(defaultValues, true);*/
    this.formTooltips = tooltips[0];
    this._getAdvancedSettings();
    this._checkBoosterPluginUpdateAvailable();
    this._getCompanyInfo();
  }
    private _getCompanyInfo(): void {
        this._builderSectionsInteractionService.companyInfo
            .pipe(
                filter(companyInfo => !!companyInfo),
                takeUntil(this._destroyStream$),
                first()
            )
            .subscribe(companyInfo => {
                this.companyName = companyInfo?.branding?.company_name || companyInfo?.agency?.name || 'YourBrand';
            });
    }

  private _getAdvancedSettings(): void {
    this._store.select(fromStore.getBoosterAdvancedSettingsLoaded(this.websiteId))
      .pipe(
        filter((loaded: boolean) => !!loaded),
        switchMap(() => this._store.select(fromStore.getBoosterAdvancedSettings(this.websiteId))),
        takeUntil(this._destroyStream$)
      )
      .subscribe(data => {
        if (!TypeUtils.isNull(data)) {
          this.loadingReset = false;
          this.excludeFiles = data["excludeFiles"];
          this.excludeCss = data["excludeCss"];
          this.excludeAggregationFiles = data["excludeAggregationFiles"];
          this.defaultExcludeFiles = data["defaultExcludeFiles"];
          this.defaultExcludeCss = data["defaultExcludeCss"];
          this.defaultExcludeAggregationFiles = data["defaultExcludeAggregationFiles"];
          this.allSettingsData = data["settings"];
          this._setFormGroupData(data["settings"]);
          this.lazyLoad = false;
        }
      });
  }

  private _setFormGroupData(settings, defaultValues?): void {
    this._createInputs(settings);
    const jsDelayOptions = Object.values(settings).slice(0, 6);
    const criticalCssOptions = Object.values(settings).slice(6, 11);
    const fontOptions = [Object.values(settings)[11]];
    const scrollOptions = [Object.values(settings)[12]];
    const lazyLoadOptions = Object.values(settings).slice(13, 17);

    // Replace "10Web Builder" with company name in all options
    const allOptions = [jsDelayOptions, criticalCssOptions, fontOptions, scrollOptions, lazyLoadOptions];
    allOptions.forEach(optionGroup => {
      optionGroup.forEach((setting: any) => {
        if (setting.options && Array.isArray(setting.options)) {
          setting.options.forEach(option => {
            if (option.label && typeof option.label === 'string') {
              option.label = option.label.replace(/10Web Builder/g, `${this.companyName} Builder`);
            }
          });
        }
      });
    });

    this.formGroupData = allOptions;
    setTimeout(() => {
      this.dataLoaded = true;
    }, defaultValues ? 2000 : 0);
  }

  private _checkBoosterPluginUpdateAvailable(): void {
    this._store.select(fromStore.getBoosterPluginInfo(this.websiteId))
      .pipe(
        filter(pluginInfo => !!pluginInfo)
      )
      .subscribe(([pluginUpdateAvailable, currentVersion]) => {
        if (!TypeUtils.isNull(pluginUpdateAvailable) && !!pluginUpdateAvailable) {
          this.boosterUpdateAvailable = true;
        }
      });
  }

  private _checkFormValueChanges(): void {
    const initialValue = this.formGroup.value;
    const cssOrJsDelayChanges = ['two_delay_all_js_execution', 'two_exclude_delay_js', 'two_delay_js_exclusions',
      'two_aggregate_js', 'two_minify_js', 'two_exclude_js',
      'two_critical_status', 'two_aggregate_css', 'two_exclude_css', 'two_include_inline_css', 'two_minify_css'];
    this.formGroup.valueChanges.subscribe(value => {
      this.disableBtn = false;
      this.hasChange = Object.keys(initialValue).some(key =>
        cssOrJsDelayChanges.includes(key) && this.formGroup.controls[key].value !== initialValue[key]);
    });
  }

  public saveAdvancedSettingsData(): void {
    if (!!this.disableOptions) return;
    if (this.demoUser) {
      const analyticsData = {
        eventCategory: 'Free Upgrade Offer',
        eventAction: 'Locked button click',
        eventLabel: 'Advanced settings'
      };
      this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
      this.showDemoUpgradeDialog();
      return;
    }
    else {
      if (this.hasChange) {
        this._showSaveSettingsDialog('text_boosterAdvancedSettingsSaveDesc' + (this.testModeEnabled ? '2' : '1'));
      } else {
        this.testModeEnabled ? this._showSaveSettingsDialog('text_boosterAdvancedSettingsSaveDesc3') : this._setAdvancedSettings();
      }
    }
  }

  private _showSaveSettingsDialog(description): void {
    this._dialogService.addDialog(NotificationDialogComponent, {
      header: TextUtils.getText('text_boosterAdvancedSettingsSaveTitle'),
      message: TextUtils.getText(description),
      buttonText: 'Save',
      buttonColor: 'green',
      action: 'save',
      hasCloseButton: true
    })
      .subscribe((confirmed: string): void => {
        if (confirmed) {
          this._setAdvancedSettings();
        }
      });
  }

  private _setAdvancedSettings(): void {
    this.loading = true;
    let formData = {};
    for (let key in this.formGroup.controls) {
      let optionValue;
      if (TypeUtils.isBoolean(this.formGroup.controls[key].value)) {
        optionValue = (!!Utils.isTruthy(this.formGroup.controls[key].value) ? 'on' : 'off');
      }
      else if (['two_exclude_delay_js', 'two_exclude_css', 'two_exclude_js'].includes(key)) {
        optionValue = (key === 'two_exclude_css' ? this.excludeCss.toString() : (key === 'two_exclude_js' ? this.excludeAggregationFiles.toString() : this.excludeFiles.toString()));
      }
      else if (key === 'two_delay_js_exclusions') {
        optionValue = this.selectedJsOptions;
      }
      else {
        optionValue = this.formGroup.controls[key].value;
      }
      formData[key] = optionValue;
    }
    this._boosterService.setAdvancedSettings(this.websiteId, formData)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(response => {
        NotifierComponent.notify(new Notification(response.message, response.success ? NotificationType.SUCCESS : NotificationType.DANGER));
        this.loading = false;
        this.disableBtn = true;
        this.lazyLoad = true;
        this._store.dispatch(new fromStore.LoadBoosterAdvancedSettings(this.websiteId));
      });
  }


  private _createInputs(inputs: any[]): void {
    // add counter for required content
    const controls = {};
    inputs.forEach(input => controls[input.name] = this._createFormControl(input));
    this.formGroup = new FormGroup(controls);
    this._checkFormValueChanges();
  }

  private _createFormControl(input: any): FormControl {
    const validators = [];
    input.required && validators.push(Validators.required);
    /* for checkboxes we get not true/false. we get on/off */
    const newValue = input.field_type === 'checkbox' ? input.value === 'on' : (input.value || '');
    const value = this.loadingReset ? input.default : newValue;
    return new FormControl(value, validators);
  }

  public addExcludedValue($event: any, name: string): void {
    const excludeFiles = name === 'two_exclude_css' ? this.excludeCss : (name === 'two_exclude_delay_js' ? this.excludeFiles : this.excludeAggregationFiles);
    if ($event.keyCode === 13 && this.addedExcludedElementValue.value !== "") {
      excludeFiles[excludeFiles.length] = this.addedExcludedElementValue.value;
      this.addedExcludedElementValue.setValue("");
      this.disableBtn = false;
      if (name === 'two_exclude_css') {
        this.excludeCss = excludeFiles;
      }
      else if (name === 'two_exclude_delay_js') {
        this.excludeFiles = excludeFiles;
      }
      else {
        this.excludeAggregationFiles = excludeFiles;
      }
    }
  }

  public removeExcludedElement(page: string, name: string): void {
    const excludeFiles = name === 'two_exclude_css' ? this.excludeCss : (name === 'two_exclude_delay_js' ? this.excludeFiles : this.excludeAggregationFiles);
    excludeFiles.splice( excludeFiles.indexOf(page), 1 );
    this.disableBtn = false;
    if (name === 'two_exclude_css') {
      this.excludeCss = excludeFiles;
    }
    else if (name === 'two_exclude_delay_js') {
      this.excludeFiles = excludeFiles;
    }
    else {
      this.excludeAggregationFiles = excludeFiles;
    }
  }

  public choosedOptions(items, type, allOptions): void {
    this.disableBtn = false;
    if (type === 'css') {
      this.selectedCssOptions = this._selectDeselectItems(items, allOptions);
    }
    else {
      this.selectedJsOptions = this._selectDeselectItems(items, allOptions);
    }
  }

  private _selectDeselectItems(items, allOptions): any {
    let selectedOptions = [];
    items.forEach((item) => {
      let selectedItem = allOptions.find(e => e.value === item.value);
      selectedOptions.push(selectedItem.value);
    });
    return selectedOptions;
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }


  @HostListener('document:mousemove', ['$event']) onMouseMove($event: any) {
    const x = $event.clientX,
      y = $event.clientY;
    if ( $event.target && $event.target.parentElement && $event.target.parentElement.classList.contains("db-advanced-settings__block")) {
      const recordItemNameTooltipSpan = $event.target.children[0];
      recordItemNameTooltipSpan.style.top = (y + 20) + 'px';
      recordItemNameTooltipSpan.style.left = (x - 260) + 'px';
    }
  }

  showDemoUpgradeDialog() {
    const firstTime = TypeUtils.isNull(localStorage.getItem('dbShowFirstTime'));
    const options = {
      overflowHidden: false,
    };
    if (!firstTime) {
      options['width'] = 1240;
    } else {
      options['width'] = 460;
      options['height'] = 600;
    }
    const modal = this._modalService.open(OneWeekFreeDemoUpgradeComponent, options);
    modal.componentInstance.showFirstTime = firstTime;
  }

  isValidOption(value: any, options: any[]): boolean {
    if (!value || !options) return false;
    const values = Array.isArray(value) ? value : [value];
    return values.every(val => options.some(option => option.value === val));
  }

}
